/** @jsxImportSource @emotion/react */
import * as naturalSort from "node-natural-sort";
import * as diacritics from "diacritics";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";
import { SpinnerBlock } from "../common/Spinner";
import VenueListFiltersForm from "./VenueListFiltersForm";
import VenueListRow from "./VenueListRow";
import * as organizationSelectors from "../organization/organizationSelectors";
import { StyledText } from "../../assets/typography/typography";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import * as authSelectors from "../auth/authSelectors";
import { useGetCountries } from "../../service/api";
import { ButtonInternalLink } from "../../components/NewButtons";
import { routes } from "../../appRoutingHelpers";
import { VenueListItem } from "../../model/VenueListItem";
import { useGetRestaurantTags, useGetRestaurants } from "./service/venue-api";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { Divider } from "../../components/Divider";
import { firstTableCellStyle } from "../../style-util";
import { PageFooter } from "../../components/page-skeleton/PageFooter";
import { TableBody } from "../../components/table-components/TableBody";
import { useHandleFetchError } from "../../error-handler";

const clearStringForSearch = (v: string): string => {
  let val = diacritics.remove(v);
  val = val.toLowerCase();
  val = val.replace(/\W+/g, "");
  return val;
};

export const VenueList = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    searchValue,
    tag,
    country,
    venueOnlyVisible,
  } = useSearchParamsManipulation();

  const { t } = useTranslation();
  const {
    data: countries,
    isLoading: isLoadingCountries,
    error: getCountriesError,
  } = useGetCountries();
  const {
    data: tags,
    isLoading: tagsAreLoading,
    error: getRestaurantTagsError,
  } = useGetRestaurantTags(organizationKeyName);
  const {
    data: restaurants,
    isLoading: restaurantsAreLoading,
    error: getRestaurantsError,
  } = useGetRestaurants(organizationKeyName);

  const getFilteredRestaurants = (): VenueListItem[] => {
    let res = (restaurants || []).filter((venue) => {
      let filtersStatus = [];

      if (searchValue && searchValue.length > 0) {
        filtersStatus.push(
          clearStringForSearch(venue.title).includes(
            clearStringForSearch(searchValue)
          )
        );
      }

      if (tag && tag.length > 0) {
        filtersStatus.push(venue.tags.includes(tag.toLowerCase()));
      }

      if (country && country.length > 0) {
        filtersStatus.push(venue.country === country);
      }

      if (venueOnlyVisible) {
        filtersStatus.push(!venue.isHidden);
      }

      return filtersStatus.every((status) => !!status);
    });

    const comparator = naturalSort({ caseSensitive: false });
    res = res.sort((a, b) => {
      if (a.isHidden && !b.isHidden) {
        return 1;
      }
      if (!a.isHidden && b.isHidden) {
        return -1;
      }
      return comparator(a.title, b.title);
    });
    return res;
  };

  const isActiveColVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListColumnVisible(state, "active")
  );
  const isAddressColVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListColumnVisible(state, "address")
  );
  const isImageColVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListColumnVisible(state, "image")
  );
  const isTagsColVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListColumnVisible(state, "tags")
  );
  const isTitleColVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListColumnVisible(state, "title")
  );
  const isEditActionVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListActionVisible(state, "edit")
  );
  const isCountryFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListFilterVisible(state, "country")
  );
  const isNameFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListFilterVisible(state, "name")
  );
  const isIsActiveFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListFilterVisible(state, "isActive")
  );
  const isTagFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isVenueListFilterVisible(state, "tag")
  );

  useHandleFetchError(getRestaurantTagsError);
  useHandleFetchError(getCountriesError);
  useHandleFetchError(getRestaurantsError);

  if (tagsAreLoading || isLoadingCountries || restaurantsAreLoading) {
    return <SpinnerBlock />;
  }

  return (
    <PageWrapper>
      <VenueListFiltersForm
        tags={tags || []}
        countries={countries || []}
        isCountryFilterVisible={isCountryFilterVisible}
        isNameFilterVisible={isNameFilterVisible}
        isIsActiveFilterVisible={isIsActiveFilterVisible}
        isTagFilterVisible={isTagFilterVisible}
      />
      <CardContainer>
        <PageHeader title="Restaurant list">
          <ButtonInternalLink to={routes.venueNew(organizationKeyName)}>
            Add new
          </ButtonInternalLink>
        </PageHeader>
        <div>
          {restaurants?.length === 0 ? (
            <>
              <Divider />
              <EmptyState
                title="No restaurants found"
                subtitle="Add a new restaurant to get started"
              />
            </>
          ) : (
            <>
              {getFilteredRestaurants().length === 0 ? (
                <>
                  <Divider />
                  <EmptyState
                    title="No restaurants found for your filters"
                    subtitle="Try changing the filters"
                  />
                </>
              ) : (
                <>
                  <Table>
                    <thead>
                      <TableRowHeader>
                        {isImageColVisible && (
                          <TableCellHeaderText css={firstTableCellStyle}>
                            {`${t("venue_list.list.header.image")}`}
                          </TableCellHeaderText>
                        )}
                        {isTitleColVisible && (
                          <TableCellHeaderText>
                            {`${t("venue_list.list.header.title")}`}
                          </TableCellHeaderText>
                        )}
                        {isAddressColVisible && (
                          <TableCellHeaderText>
                            {`${t("venue_list.list.header.address")}`}
                          </TableCellHeaderText>
                        )}
                        {isTagsColVisible && (
                          <TableCellHeaderText>
                            {`${t("venue_list.list.header.tags")}`}
                          </TableCellHeaderText>
                        )}
                        {isActiveColVisible && (
                          <TableCellHeaderText>
                            Visible webshop & app
                          </TableCellHeaderText>
                        )}
                      </TableRowHeader>
                    </thead>
                    <TableBody>
                      {getFilteredRestaurants().map((venue) => (
                        <VenueListRow
                          organization={organizationKeyName}
                          key={venue.id}
                          venue={venue}
                          isActiveColVisible={isActiveColVisible}
                          isAddressColVisible={isAddressColVisible}
                          isImageColVisible={isImageColVisible}
                          isTagsColVisible={isTagsColVisible}
                          isTitleColVisible={isTitleColVisible}
                          isEditActionVisible={isEditActionVisible}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
          {getFilteredRestaurants().length > 0 && (
            <PageFooter>
              <StyledText className="bodyTextRegular mb-0">
                {`${
                  getFilteredRestaurants().length === 1
                    ? "1 restaurant"
                    : `${getFilteredRestaurants().length} restaurants`
                }`}
              </StyledText>
            </PageFooter>
          )}
        </div>
      </CardContainer>
    </PageWrapper>
  );
};
